import { yupResolver } from "@hookform/resolvers/yup";
import { ClassicHeroContent, DEFAULT_SECTION_MARGINS, SectionMargins } from "api/shop";
import {
  HeadingTagSelect,
  PaziaFormButton,
  PaziaInput,
  PaziaFileDropzone,
  PaziaSelect,
  PaziaCheckbox,
} from "components/atoms";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";
import { useShopImageUpload } from "./useShopImageUpload";

interface ClassicHeroFormProps {
  onSubmit: (data: ClassicHeroFormValues) => void;
  onCancel: () => void;
  defaultValues?: ClassicHeroContent;
  isLoading?: boolean;
}

export const ClassicHeroForm = ({ onSubmit, onCancel, defaultValues, isLoading }: ClassicHeroFormProps) => {
  const { uploadImage } = useShopImageUpload();
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.HEADER_TEXT]: defaultValues?.header?.text ?? "",
      [FORM_NAMES.HEADER_TAG]: defaultValues?.header?.tag ?? "h2",
      [FORM_NAMES.USE_CATEGORY_BREADCRUMBS]: defaultValues?.useCategoryBreadcrumbs ?? false,
      [FORM_NAMES.BREADCRUMBS]: defaultValues?.breadcrumbs?.map((bc) => ({
        label: bc.label,
        href: bc.href ?? "",
      })) ?? [],
      [FORM_NAMES.BG_IMAGE_SRC]: defaultValues?.bgImage?.src ?? "",
      [FORM_NAMES.BG_IMAGE_ALT]: defaultValues?.bgImage?.alt ?? "",
      [FORM_NAMES.BG_IMAGE_POSITION]: defaultValues?.bgImage?.position ?? "center",
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control } = methods;
  const useCategoryBreadcrumbs = methods.watch(FORM_NAMES.USE_CATEGORY_BREADCRUMBS);

  const {
    fields: breadcrumbFields,
    append: appendBreadcrumb,
    remove: removeBreadcrumb,
  } = useFieldArray({
    control,
    name: FORM_NAMES.BREADCRUMBS,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const uploadedSrc = await uploadImage(data.bgImageSrc);
    const formattedData: ClassicHeroFormValues = {
      header: { text: data.headerText, tag: data.headerTag as any },
      useCategoryBreadcrumbs: data.useCategoryBreadcrumbs ?? false,
      breadcrumbs: data.useCategoryBreadcrumbs
        ? []
        : (data.breadcrumbs ?? []).map((bc) => ({
            label: bc.label,
            href: bc.href || null,
          })),
      bgImage: {
        src: uploadedSrc,
        alt: data.bgImageAlt,
        position: data.bgImagePosition,
      },
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addBreadcrumb = () => {
    appendBreadcrumb({ label: "", href: "" });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        {/* Header */}
        <PaziaInput name={FORM_NAMES.HEADER_TEXT} label="Nagłówek" required />
        <HeadingTagSelect name={FORM_NAMES.HEADER_TAG} />

        {/* Breadcrumbs Section */}
        <div>
          <PaziaCheckbox
            name={FORM_NAMES.USE_CATEGORY_BREADCRUMBS}
            label="Użyj breadcrumbs z kategorii"
          />
          {useCategoryBreadcrumbs ? (
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Breadcrumbs zostaną wygenerowane automatycznie na podstawie drzewa kategorii przypisanej do tej strony.
            </p>
          ) : (
            <>
              <div className="mt-3 mb-3 flex items-center justify-between">
                <h4 className="font-medium text-slate-700 dark:text-slate-300">Breadcrumbs</h4>
                <button
                  type="button"
                  onClick={addBreadcrumb}
                  className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
                >
                  <MdAdd size={18} />
                  Dodaj
                </button>
              </div>
              {breadcrumbFields.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Brak breadcrumbs. Kliknij "Dodaj", aby dodać.
                </p>
              ) : (
                <div className="space-y-2">
                  {breadcrumbFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="flex items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800"
                    >
                      <div className="flex-1">
                        <PaziaInput
                          name={`${FORM_NAMES.BREADCRUMBS}.${index}.label`}
                          label="Etykieta"
                          required
                        />
                      </div>
                      <div className="flex-1">
                        <PaziaInput
                          name={`${FORM_NAMES.BREADCRUMBS}.${index}.href`}
                          label="Link (href)"
                          placeholder="Puste = brak linku"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeBreadcrumb(index)}
                        className="mb-1 rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                        title="Usuń"
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Background Image */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Obraz w tle</h4>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="[&_img]:max-h-48">
              <PaziaFileDropzone
                name={FORM_NAMES.BG_IMAGE_SRC}
                label="Obraz"
                type="image"
                single
                preview="image"
              />
            </div>
            <PaziaInput
              name={FORM_NAMES.BG_IMAGE_ALT}
              label="Tekst alternatywny (alt)"
              required
            />
            <PaziaSelect
              name={FORM_NAMES.BG_IMAGE_POSITION}
              label="Pozycja obrazu"
              options={BG_IMAGE_POSITION_OPTIONS}
            />
          </div>
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  HEADER_TEXT: "headerText",
  HEADER_TAG: "headerTag",
  USE_CATEGORY_BREADCRUMBS: "useCategoryBreadcrumbs",
  BREADCRUMBS: "breadcrumbs",
  BG_IMAGE_SRC: "bgImageSrc",
  BG_IMAGE_ALT: "bgImageAlt",
  BG_IMAGE_POSITION: "bgImagePosition",
} as const;

const breadcrumbSchema = yup.object({
  label: yup.string().required("Etykieta jest wymagana"),
  href: yup.string().nullable(),
});

const schema = yup
  .object({
    [FORM_NAMES.HEADER_TEXT]: yup.string().required("Nagłówek jest wymagany"),
    [FORM_NAMES.HEADER_TAG]: yup.string(),
    [FORM_NAMES.USE_CATEGORY_BREADCRUMBS]: yup.boolean().optional(),
    [FORM_NAMES.BREADCRUMBS]: yup.array().of(breadcrumbSchema).optional(),
    [FORM_NAMES.BG_IMAGE_SRC]: yup.mixed().required("Obraz w tle jest wymagany"),
    [FORM_NAMES.BG_IMAGE_ALT]: yup.string().required("Tekst alternatywny jest wymagany"),
    [FORM_NAMES.BG_IMAGE_POSITION]: yup.string().optional(),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

const BG_IMAGE_POSITION_OPTIONS = [
  { value: "center", label: "Środek" },
  { value: "top", label: "Góra" },
  { value: "bottom", label: "Dół" },
  { value: "left", label: "Lewo" },
  { value: "right", label: "Prawo" },
  { value: "top left", label: "Góra lewo" },
  { value: "top right", label: "Góra prawo" },
  { value: "bottom left", label: "Dół lewo" },
  { value: "bottom right", label: "Dół prawo" },
];

interface FormValues {
  headerText: string;
  headerTag: string;
  useCategoryBreadcrumbs?: boolean;
  breadcrumbs: Array<{ label: string; href: string }>;
  bgImageSrc: string | File;
  bgImageAlt: string;
  bgImagePosition?: string;
  margins: SectionMargins;
}

export interface ClassicHeroFormValues {
  header: { text: string; tag?: string };
  useCategoryBreadcrumbs?: boolean;
  breadcrumbs: Array<{ label: string; href: string | null }>;
  bgImage: { src: string; alt: string; position?: string };
  margins?: SectionMargins;
}
